import { useCallback, useEffect, useMemo, useState } from 'react';

import type { GroupBase } from 'react-select';

import isEqual from 'fast-deep-equal';

import { isDefined, isNotDefined } from '@enterprise/core';

import type { CreatableSelectProps } from '#select/types/creatable-select.props';

import { MAX_NUMBER } from '#constants/number';
import { newOption } from '#select/utilities/new-option.util';
import { parseNumericRange } from '#select/utilities/parse-numeric-range.util';
import { isMultiData } from '#types/guards/is-multi-data.guard';

/**
 * Custom React hook to manage the configuration and state for a creatable select component.
 *
 * This hook handles the creation of new options, enforces a maximum number of selectable items,
 * validates new options, and manages the state of created options and exceeded limits.
 *
 * @template IsMulti - Indicates if the select is multi-select.
 * @template Option - The type of the option object.
 * @template Group - The type of the group object, extending GroupBase<Option>.
 *
 * @param params - Configuration object.
 * @param params.getNewOptionData - Function to generate a new option object from input.
 * @param params.maxItems - Maximum number of selectable items (defaults to MAX_NUMBER).
 * @param params.onExceeded - Callback invoked when the maximum number of items is exceeded.
 * @param params.onOptionsCreated - Callback invoked when new options are created.
 * @param params.parseRange - Optional custom function to parse input into options.
 * @param params.validateNewOption - Optional function to validate a new option.
 * @param params.value - The current value(s) of the select component.
 *
 * @returns An object containing:
 *   - createdOptions: The list of options created by the user.
 *   - exceeded: The number of items exceeded, or null if not exceeded.
 *   - handleCreateOptions: Function to handle creation of new options from input.
 */
export function useCreatableSelectConfiguration<
  IsMulti extends boolean = false,
  Option = unknown,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  getNewOptionData,
  maxItems = MAX_NUMBER,
  onExceeded,
  onOptionsCreated,
  parseRange,
  validateNewOption,
  value,
}: Pick<
  CreatableSelectProps<IsMulti, Option, Group>,
  'getNewOptionData' | 'maxItems' | 'onExceeded' | 'onOptionsCreated' | 'parseRange' | 'validateNewOption' | 'value'
>) {
  const [createdOptions, setCreatedOptions] = useState<(Group | Option)[]>([]);
  const [exceeded, setExceeded] = useState<null | number>(null);

  const parser = useMemo(() => {
    return isNotDefined(parseRange) ? parseNumericRange : parseRange;
  }, [parseRange]);

  const handleCreateOptions = useCallback(
    (inputValue: string) => {
      const parsed = parser(inputValue.split(','));
      const current = isMultiData(value) ? value.length : isDefined(value) ? 1 : 0;

      if (parsed.length + current > maxItems) {
        setExceeded(parsed.length + current);
        onExceeded?.(true);

        return;
      }

      const options = parsed
        .filter((option) => {
          return validateNewOption?.(option) ?? true;
        })
        .map((option) => {
          if (isNotDefined(getNewOptionData)) {
            return newOption<Option>(option, option);
          }

          return getNewOptionData(option, option);
        });

      const filtered = options.filter((option) => {
        return isNotDefined(
          createdOptions.find((created) => {
            return isEqual(option, created);
          })
        );
      });

      onOptionsCreated?.(filtered);
      setCreatedOptions([...createdOptions, ...filtered]);
    },
    [createdOptions, getNewOptionData, maxItems, onExceeded, onOptionsCreated, parser, validateNewOption, value]
  );

  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout> | undefined;

    if (isDefined(exceeded)) {
      timerId = setTimeout(() => {
        setExceeded(null);
      }, 5000);
    }

    return () => {
      if (isDefined(timerId)) {
        clearTimeout(timerId);
      }
    };
  }, [exceeded]);

  return { createdOptions, exceeded, handleCreateOptions };
}
